import React, { useCallback, useEffect, useMemo, useState } from "react";

import * as api from "../../api";
import "../../stylesheets/Table.css";
import Page from "../HelperComponents/Page";
import VtUserManagementAddUser from "./VtUserManagementAddUser";
import VtUserManagementRow from "./VtUserManagementRow";
import VtUserManagementSearch from "./VtUserManagementSearch";
import VtUserManagementSort from "./VtUserManagementSort";

const VtUserManagementTable = (props) => {
  const { loading, setSpinner } = props;
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showDisabledUsers, setShowDisabledUsers] = useState(false);
  const [error, setError] = useState(false);
  const [sort, setSort] = useState({ sortId: "email", asc: true });
  const [pageSize, setPageSize] = useState({
    value: 10,
    label: "10 Results/Page",
  });
  const [pageNum, setPageNum] = useState(0);

  const loadUsers = useCallback(() => {
    setSpinner(true);
    api.getVtUserManagementUsers().then((response) => {
      setUsers(response && response.users ? response.users : []);
      setError(response && response.error ? true : false);
      setSpinner(false);
    });
  }, [setSpinner]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setPageNum(0);
  }, [search, sort, showDisabledUsers]);

  const visibleUsers = useMemo(() => {
    if (showDisabledUsers) {
      return users;
    }

    return users.filter((user) => (user.status || "enabled") !== "disabled");
  }, [users, showDisabledUsers]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return visibleUsers;
    }

    return visibleUsers.filter((user) =>
      [user.email, user.firstName, user.lastName, user.status].some((value) =>
        (value || "").toLowerCase().includes(query)
      )
    );
  }, [visibleUsers, search]);

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((left, right) => {
      const leftValue = (left[sort.sortId] || "").toLowerCase();
      const rightValue = (right[sort.sortId] || "").toLowerCase();

      if (leftValue === rightValue) {
        return 0;
      }

      if (sort.asc) {
        return leftValue > rightValue ? 1 : -1;
      }

      return leftValue > rightValue ? -1 : 1;
    });
  }, [filteredUsers, sort]);

  const pagedUsers = sortedUsers.slice(
    pageNum * pageSize.value,
    pageNum * pageSize.value + pageSize.value
  );

  const getRows = () => {
    if (error) {
      return (
        <tr>
          <td colSpan="5">
            <div id="no-results-found">Error loading users</div>
          </td>
        </tr>
      );
    }

    if (loading && users.length === 0) {
      return (
        <tr>
          <td colSpan="5">
            <div id="no-results-found">Loading...</div>
          </td>
        </tr>
      );
    }

    if (pagedUsers.length === 0) {
      return (
        <tr>
          <td colSpan="5">
            <div id="no-results-found">No results found</div>
          </td>
        </tr>
      );
    }

    return pagedUsers.map((user, i) => (
      <VtUserManagementRow
        key={user.id || user.subId || i}
        index={i}
        user={user}
        onUpdated={loadUsers}
      />
    ));
  };

  return (
    <div className="content vt-user-management">
      <div className="vt-user-management-toolbar">
        <label className="vt-user-management-show-disabled">
          <input
            type="checkbox"
            checked={showDisabledUsers}
            onChange={(event) => setShowDisabledUsers(event.target.checked)}
          />
          Show disabled users
        </label>
        <VtUserManagementAddUser onCreated={loadUsers} />
      </div>
      <table>
        <tbody className="vt-user-management">
          <VtUserManagementSearch search={search} setSearch={setSearch} />
          <VtUserManagementSort sort={sort} setSort={setSort} />
          {getRows()}
        </tbody>
      </table>
      <Page
        pageSize={pageSize}
        onPageSize={(e) => {
          let firstData = pageNum * pageSize.value + 1;
          let newPageNum = Math.floor(firstData / e.value);
          setPageNum(newPageNum);
          setPageSize(e);
        }}
        pageNum={pageNum}
        onPageChange={(e) => setPageNum(e)}
        numElements={pagedUsers.length}
        totalElements={sortedUsers.length}
        loading={loading}
        table="vt-user-management"
      />
    </div>
  );
};

export default VtUserManagementTable;
